import { EFeatureFlag } from "@frontend-shared/constants";
import { useMemo } from "react";

import { useAppConfig } from "@/api/app-config/useAppConfig";
import { useGetOrganizationModelTokenLimits } from "@/api/organization/useGetOrganizationModelTokenLimits";
import { useAgentsEnabled } from "@/hooks/use-agents-enabled";
import { useAnonymizationLabEnabled } from "@/hooks/use-anonymization-lab-enabled";
import { useBrandedPresentationsEnabled } from "@/hooks/use-branded-presentations-enabled";
import { useCallAssistantFeaturesEnabled } from "@/hooks/use-call-assistant-features-enabled";
import { useCustomIntegrationEnabled } from "@/hooks/use-custom-integration-enabled";
import { useDocRecognitionEnabled } from "@/hooks/use-doc-recognition-enabled";
import { useDomainVerificationAvailable } from "@/hooks/use-domain-verification-available";
import { useExternalApiEnabled } from "@/hooks/use-external-api-enabled";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useFinancialMaskingFeatureEnabled } from "@/hooks/use-financial-masking-feature-enabled";
import { useIsBillingDisabled } from "@/hooks/use-is-billing-disabled";
import { useOnPremise } from "@/hooks/use-on-premise";
import { usePiiAdvancedRulesFeatureEnabled } from "@/hooks/use-pii-advanced-rules-feature-enabled";
import { usePiiCustomEntitiesFeatureEnabled } from "@/hooks/use-pii-custom-entities-feature-enabled";
import { usePiiIntentRoutingFeatureEnabled } from "@/hooks/use-pii-intent-routing-feature-enabled";
import { useSamlSeatTiersEnabled } from "@/hooks/use-saml-seat-tiers-enabled";
import { useSiemExportEnabled } from "@/hooks/use-siem-export-enabled";
import { useSkillsEnabled } from "@/hooks/use-skills-enabled";
import { useSyncatorApiEnabled } from "@/hooks/use-syncator-api-enabled";
import { useTokenCostUsageEnabled } from "@/hooks/use-token-cost-usage-enabled";
import { useTokenLimitsEnabled } from "@/hooks/use-token-limits-enabled";
import { useTokenUsageEnabled } from "@/hooks/use-token-usage-enabled";
import { useTtsConfig } from "@/hooks/use-tts-config";
import { useUserMemoryEnabled } from "@/hooks/use-user-memory-enabled";
import type { IGlobalSearchFlagInputs } from "@/lib/global-search/types";

import type { IUseGlobalSearchFlagInputsProps } from "../types";

// NOTE: NSAI-6589 — every deployment switch a static index gate reads, through
// the same hooks the gated pages and settings sections use.
export const useGlobalSearchFlagInputs = ({
  ownedOrganizationUuid,
}: IUseGlobalSearchFlagInputsProps): IGlobalSearchFlagInputs => {
  const { data: appConfig } = useAppConfig();
  const { isOnPremise } = useOnPremise();
  const { isBillingDisabled } = useIsBillingDisabled();
  const { isExternalApiEnabled } = useExternalApiEnabled();
  const { isTtsEnabled, ttsVoices } = useTtsConfig();
  // NOTE: Only an owner has the Usage Limits editor this flag gates, so the
  // endpoint is asked only for an organization the caller owns.
  const { data: modelTokenLimits } = useGetOrganizationModelTokenLimits(
    ownedOrganizationUuid,
  );
  const isAgentsEnabled = useAgentsEnabled();
  const isAnonymizationLabEnabled = useAnonymizationLabEnabled();
  const isDocRecognitionEnabled = useDocRecognitionEnabled();
  const isSkillsEnabled = useSkillsEnabled();
  const isCustomIntegrationEnabled = useCustomIntegrationEnabled();
  const isCallAssistantEnabled = useCallAssistantFeaturesEnabled();
  const isUserMemoryEnabled = useUserMemoryEnabled();
  const isWhatsappBotEnabled = useFeatureFlag(EFeatureFlag.WHATSAPP_BOT);
  const isTokenUsageEnabled = useTokenUsageEnabled();
  const isTokenCostUsageEnabled = useTokenCostUsageEnabled();
  const isTokenLimitsEnabled = useTokenLimitsEnabled();
  const isSyncatorApiEnabled = useSyncatorApiEnabled();
  const isSiemExportEnabled = useSiemExportEnabled();
  const isBrandedPresentationsEnabled = useBrandedPresentationsEnabled();
  const isFinancialMaskingEnabled = useFinancialMaskingFeatureEnabled();
  const isPiiCustomEntitiesEnabled = usePiiCustomEntitiesFeatureEnabled();
  const isPiiAdvancedRulesEnabled = usePiiAdvancedRulesFeatureEnabled();
  const isPiiIntentRoutingEnabled = usePiiIntentRoutingFeatureEnabled();
  const isSamlSeatTiersEnabled = useSamlSeatTiersEnabled();
  const isDomainVerificationAvailable = useDomainVerificationAvailable();

  return useMemo(
    () => ({
      isOnPremise,
      isBillingDisabled,
      isAgentsEnabled,
      isAnonymizationLabEnabled,
      isDocRecognitionEnabled,
      isSkillsEnabled,
      isCustomIntegrationEnabled,
      isCallAssistantEnabled,
      isExternalApiEnabled,
      isUserMemoryEnabled,
      isSlackBotEnabled: Boolean(appConfig?.slackBotUri),
      isTeamsBotEnabled: Boolean(appConfig?.teamsBotUri),
      isMatrixBotEnabled: Boolean(appConfig?.isMatrixBotEnabled),
      isTelegramBotEnabled: Boolean(appConfig?.isTelegramBotEnabled),
      isWhatsappBotEnabled,
      isTokenUsageEnabled,
      isTokenCostUsageEnabled,
      isTokenLimitsEnabled,
      isTtsEnabled,
      ttsVoiceCount: ttsVoices.length,
      isSyncatorApiEnabled,
      isSiemExportEnabled,
      isBrandedPresentationsEnabled,
      isFinancialMaskingEnabled,
      isPiiCustomEntitiesEnabled,
      isPiiAdvancedRulesEnabled,
      isPiiIntentRoutingEnabled,
      isSamlSeatTiersEnabled,
      isDomainVerificationAvailable,
      isModelTokenLimitsEnabled: modelTokenLimits?.isEnabled === true,
    }),
    [
      appConfig,
      isOnPremise,
      isBillingDisabled,
      isAgentsEnabled,
      isAnonymizationLabEnabled,
      isDocRecognitionEnabled,
      isSkillsEnabled,
      isCustomIntegrationEnabled,
      isCallAssistantEnabled,
      isExternalApiEnabled,
      isUserMemoryEnabled,
      isWhatsappBotEnabled,
      isTokenUsageEnabled,
      isTokenCostUsageEnabled,
      isTokenLimitsEnabled,
      isTtsEnabled,
      ttsVoices,
      isSyncatorApiEnabled,
      isSiemExportEnabled,
      isBrandedPresentationsEnabled,
      isFinancialMaskingEnabled,
      isPiiCustomEntitiesEnabled,
      isPiiAdvancedRulesEnabled,
      isPiiIntentRoutingEnabled,
      isSamlSeatTiersEnabled,
      isDomainVerificationAvailable,
      modelTokenLimits,
    ],
  );
};
